import {
    HttpClient,
    HttpErrorResponse,
    HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    GroupRideDeviceContract,
    GroupRideDeviceProfileContract,
    GroupRideDevicesResponseContract,
    GroupRideGroupCreateContract,
    GroupRideGroupMemberUpdateContract,
    GroupRideGroupSummaryContract,
    GroupRideJoinedGroupContract,
    GroupRideJoinedGroupDeviceContract,
    GroupRideRiderLocationUpdateContract,
    GroupRideSendMessagesResponseContract,
    RiderGroupCurrentStateContract,
} from '@polaris-customerweb/sharedcomponents.core';
import { RoutingHelpersService } from '@polaris-customerweb/sharedcomponents.shared';
import { cloneDeep } from 'lodash-es';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RideGroup } from 'src/app/models/ride-group';
import { RideGroupRiderStatus } from '../models/ride-group-rider-status';

@Injectable({ providedIn: 'root' })
export class RideGroupResource {
    private baseUrl: string;
    private maxMessageCount = '200';

    constructor(
        configService: ConfigService,
        private http: HttpClient,
        private routingHelpersService: RoutingHelpersService
    ) {
        this.baseUrl = routingHelpersService.combineUrl(
            configService.apiUrl.rideCommand,
            'group-ride'
        );
    }

    // The minutes since active for calculating the active device/rider counts (default is 1 minute).
    getMyGroups(
        minutesSinceActive?: number | null
    ): Observable<GroupRideJoinedGroupContract[]> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'my-groups'
        );
        let params = new HttpParams();
        if (minutesSinceActive) {
            params = params.set(
                'minutesSinceActive',
                String(minutesSinceActive)
            );
        }

        return this.http.get<GroupRideJoinedGroupContract[]>(url, {
            params,
        });
    }

    getByLocation(
        lat: number,
        lng: number,
        distanceMeters = 10000
    ): Observable<GroupRideGroupSummaryContract[]> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'group'
        );
        const params = new HttpParams()
            .set('latitude', String(lat))
            .set('longitude', String(lng))
            .set('metersRadius', String(distanceMeters));

        return this.http.get<GroupRideGroupSummaryContract[]>(url, {
            params,
        });
    }

    getById(id: string): Observable<RideGroup> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'group',
            id
        );
        return this.http
            .get<GroupRideGroupSummaryContract>(url)
            .pipe(map((contract) => new RideGroup(contract)));
    }

    getByInviteId(inviteId: string): Observable<RideGroup> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'invite',
            inviteId,
            'group'
        );
        return this.http
            .get<GroupRideGroupSummaryContract>(url)
            .pipe(map((contract) => new RideGroup(contract)));
    }

    getRideGroupStatusItems(
        groupId: string
    ): Observable<GroupRideRiderLocationUpdateContract[]> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'status-item'
        );
        const params = new HttpParams().set('groupFilter', groupId);

        return this.http
            .get<RiderGroupCurrentStateContract>(url, { params })
            .pipe(
                map((statusResponse) => {
                    return statusResponse.membersAndFriends?.features || [];
                })
            );
    }

    getRideGroupStatus(
        rideGroupId: string,
        includeMessagesAfter: string | undefined
    ): Observable<RiderGroupCurrentStateContract> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'status-item'
        );
        let params = new HttpParams().set('groupFilter', rideGroupId);

        if (includeMessagesAfter) {
            // Succeeding group ride message call
            params = params.set('messagesAfter', includeMessagesAfter);
        } else {
            // Initial group ride message call
            params = params.set('maxMessageCount', this.maxMessageCount);
        }

        return this.http.get<RiderGroupCurrentStateContract>(url, { params });
    }

    postRiderStatus(
        rideGroupId: string,
        riderToFollow: RideGroupRiderStatus | undefined,
        includeMessagesAfter: string | undefined
    ): Observable<RiderGroupCurrentStateContract> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'status-item'
        );
        let params = new HttpParams().set('groupFilter', rideGroupId);
        if (includeMessagesAfter) {
            // Succeeding group ride message call
            params = params.set('messagesAfter', includeMessagesAfter);
        } else {
            // Initial group ride message call
            params = params.set('maxMessageCount', this.maxMessageCount);
        }

        const data = riderToFollow
            ? riderToFollow.toRideGroupStatusRequest()
            : {};

        return this.http.post<RiderGroupCurrentStateContract>(url, data, {
            params,
        });
    }

    createGroup(
        groupContract: GroupRideGroupCreateContract
    ): Observable<{ id: string }> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'group'
        );

        return this.http.post<{ id: string }>(url, groupContract).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error) {
                    return throwError(() => error.error as unknown);
                }

                return throwError(() => error);
            })
        );
    }

    joinGroup(
        group: RideGroup,
        passcode?: string,
        inviteId?: string
    ): Observable<RideGroup> {
        const groupContract: GroupRideGroupMemberUpdateContract = {
            inviteCode: inviteId,
            passcode,
            status: 'Active',
        };
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'group',
            group.groupId,
            'member'
        );

        return this.http
            .put<null>(url, groupContract)
            .pipe(map(() => cloneDeep(group)));
    }

    leaveGroup(id: string): Observable<void> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'group',
            id,
            'member'
        );

        return this.http.delete<undefined>(url);
    }

    sendMessage(
        groupId: string,
        message: string
    ): Observable<GroupRideSendMessagesResponseContract> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'messages'
        );
        return this.http.post(url, {
            groupId,
            messages: [
                {
                    messageSegments: [message],
                    sentAt: new Date().getTime(),
                },
            ],
        });
    }

    getGroupRideDevices(
        reportedSince?: Date
    ): Observable<GroupRideDeviceContract[]> {
        let params = new HttpParams();
        if (reportedSince) {
            params = params.set('reportedSince', reportedSince.toISOString());
        }
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'devices'
        );
        return this.http
            .get<GroupRideDevicesResponseContract>(url, { params })
            .pipe(map((result) => result.devices || []));
    }

    getDeviceProfile(
        deviceId?: string,
        id?: string
    ): Observable<GroupRideDeviceProfileContract> {
        let params = new HttpParams();
        if (deviceId) {
            params = params.set('deviceId', deviceId);
        }
        if (id) {
            params = params.set('id', id);
        }
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'device-profile'
        );
        return this.http.get<GroupRideDeviceProfileContract>(url, {
            params,
        });
    }

    getDeviceProfiles(): Observable<GroupRideDeviceProfileContract[]> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'device-profiles'
        );
        return this.http.get<GroupRideDeviceProfileContract[]>(url);
    }

    saveDeviceProfile(
        deviceProfile: GroupRideDeviceProfileContract
    ): Observable<GroupRideDeviceProfileContract> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'device-profiles'
        );
        return this.http.post<GroupRideDeviceProfileContract>(
            url,
            deviceProfile
        );
    }

    updateDeviceProfile(
        deviceProfile: GroupRideDeviceProfileContract
    ): Observable<GroupRideDeviceProfileContract> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'device-profiles'
        );
        return this.http.put<GroupRideDeviceProfileContract>(
            url,
            deviceProfile
        );
    }

    getDeviceSharing(
        deviceStatus: GroupRideJoinedGroupDeviceContract[]
    ): Observable<GroupRideJoinedGroupDeviceContract[]> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'group/device-sharing'
        );
        return this.http.post<GroupRideJoinedGroupDeviceContract[]>(
            url,
            deviceStatus
        );
    }

    updateDeviceSharing(
        deviceStatus: GroupRideJoinedGroupDeviceContract[]
    ): Observable<GroupRideJoinedGroupDeviceContract[]> {
        const url = this.routingHelpersService.combineUrl(
            this.baseUrl,
            'group/device-sharing'
        );
        return this.http.put<GroupRideJoinedGroupDeviceContract[]>(
            url,
            deviceStatus
        );
    }
}
